import { Link, Script } from "gatsby"
import React from "react"
import SanityImage from "gatsby-plugin-sanity-image"
import {
  RegBody,
  SanityImg,
  SemiBdTitle4,
  BdTitle2,
  iconMapper,
  IconName,
  Button,
} from "@sr/gatsby-theme-ui"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"

declare global {
  interface Window {
    Tourial: any
  }
}
export interface ProductEntryHeroProps {
  productTitle: string
  hero: {
    header: string
    img: SanityImg
    intro: string
    on: boolean
    primaryButtonLink: {
      reference: {
        path: {
          current: string
        }
      }
    }
    primaryButtonText: string
    secondaryButtonLinkType?: "none" | "tourial" | "internal"
    secondaryButtonText?: string
    secondaryButtonInternalLink?: {
      reference: {
        path: {
          current: string
        }
      }
    }
    tourialId?: string
  }
  icon: IconName
}

const ProductEntryHero = ({
  hero,
  productTitle = "Product",
  icon,
}: ProductEntryHeroProps) => {
  if (!hero?.on) return null

  return (
    <ProductEntryHeroStyled>
      <div className="product-entry-hero__intro-container">
        <SemiBdTitle4 className="product-entry-hero__intro-product-title">
          <div className="product-entry-hero__intro-icon">
            {iconMapper(icon, { color: "var(--color-primary)", size: 16 })}
          </div>
          {productTitle && productTitle}
        </SemiBdTitle4>
        <BdTitle2 className="product-entry-hero__intro-header">
          {hero?.header}
        </BdTitle2>
      </div>
      <div className="product-entry-hero__image-container">
        {hero?.img?.asset && (
          <SanityImage
            alt={hero?.img.alt || "hero image"}
            asset={hero?.img.asset}
            className="product-entry-hero__image"
            crop={hero?.img.crop}
            hotspot={hero?.img.hotspot}
            width={375}
            height={408}
          />
        )}
      </div>
      <div className="product-entry-hero__content-container">
        <RegBody className="product-entry-hero__content-intro-text">
          {hero?.intro}
        </RegBody>
        <div className="product-entry-hero__btn-container">
          <Button expand btnColor="var(--color-primary)">
            <Link
              className="product-entry-hero__link link-primary"
              to={hero?.primaryButtonLink?.reference?.path?.current}
            >
              {hero?.primaryButtonText}
            </Link>
          </Button>
          {hero?.secondaryButtonLinkType === "internal" &&
            hero?.secondaryButtonText &&
            hero?.secondaryButtonInternalLink && (
              <Button expand className="product-entry-hero__button button-secondary">
              <Link
                className="product-entry-hero__link link-secondary"
                to={hero?.secondaryButtonInternalLink?.reference?.path?.current}
              >
                  {hero?.secondaryButtonText}
              </Link>
                </Button>
            )}
          {hero?.secondaryButtonLinkType === "tourial" && hero?.tourialId && (
            // <Button className="" expand  btnColor="var(--color-smartRentPrimaryOnDark)"><a href={`https://demos.smartrent.com/${hero?.tourialId}`} target="_blank">Take a Tour</a></Button>
            <a
              href={`https://demos.smartrent.com/pages/${hero?.tourialId}`}
              target="_blank"
              className="product-entry-hero__anchor"
            >
              <Button btnColor="#373e47" className="link-secondary" expand>
                Interactive Tour
              </Button>
            </a>
            // <>
            //   <Script
            //     type="text/javascript"
            //     src="https://demos.smartrent.com/lightbox.js"
            //     // @ts-ignore
            //     tourialSlug={hero?.tourialId}
            //     isMobileVariantEnabled="true"
            //     baseUrlWithSubdomain="https://demos.smartrent.com/"
            //     viewportHeader="false"
            //     id="tourial-lightbox-script"
            //   />

            //   <a
            //     className={`product-entry-hero__link link-secondary tourial-lightbox-trigger-${hero?.tourialId}`}
            //     onClick={() =>
            //       window.Tourial
            //         ? window.Tourial.openLightbox(
            //             "https://demos.smartrent.com/",
            //             hero?.tourialId
            //           )
            //         : null
            //     }
            //   >
            //     <div className="product-entry-hero__button button-secondary">
            //       Take a Tour
            //     </div>
            //   </a>
            // </>
          )}
        </div>
      </div>
    </ProductEntryHeroStyled>
  )
}

export default ProductEntryHero

const ProductEntryHeroStyled = styled.div`
  padding: ${rem(48)} 0 ${rem(40)};
  box-shadow: 0px 4px 8px rgba(45, 50, 57, 0.2);

  @media ${media.midUp} {
    padding-bottom: ${rem(56)};
    margin: ${rem(48)} ${rem(64)} 0;
    border-radius: 8px;
  }

  @media ${media.stMidUp} {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    height: ${rem(576)};
    width: 100%;
    max-width: ${rem(1312)};
    padding: 0;
    margin: ${rem(96)} ${rem(64)} 0;
  }

  .product-entry-hero__intro-container {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
    padding: 0 ${rem(16)} ${rem(48)};

    @media ${media.midUp} {
      padding: 0 ${rem(32)} ${rem(48)};
    }

    @media ${media.stMidUp} {
      gap: 0;
      order: 1;
      padding: ${rem(80)} 0 0 ${rem(56)};
    }

    .product-entry-hero__intro-product-title {
      display: flex;
      align-items: center;
      gap: ${rem(16)};
      color: var(--color-primary);

      @media ${media.midUp} {
        font-size: ${rem(24)};
      }

      .product-entry-hero__intro-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: ${rem(32)};
        height: ${rem(32)};
        border-radius: ${rem(16)};
        border: 1px solid var(--color-primary);
      }
    }

    .product-entry-hero__intro-header {
      max-width: ${rem(343)};

      @media ${media.midUp} {
        max-width: ${rem(584)};
        font-size: ${rem(56)};
        line-height: ${rem(72)};
      }
    }
  }

  .product-entry-hero__image-container {
    padding-bottom: ${rem(40)};
    width: 100%;

    @media ${media.stMidUp} {
      width: ${rem(530)};
      order: 3;
      align-self: flex-end;
      border-radius: 0 ${rem(8)} ${rem(8)} 0;
      overflow: hidden;
    }
  }

  .product-entry-hero__content-container {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
    padding: 0 ${rem(16)};

    @media ${media.midUp} {
      padding: 0 ${rem(32)};
    }

    @media ${media.stMidUp} {
      padding: ${rem(16)} 0 0 ${rem(56)};
      order: 2;
    }

    .product-entry-hero__content-intro-text {
      @media ${media.midUp} {
        max-width: ${rem(483)};
      }

      @media ${media.stMidUp} {
        max-width: ${rem(411)};
      }
    }

    .product-entry-hero__btn-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: ${rem(16)};
      font-weight: 600;
      width: 100%;

      @media ${media.midUp} {
        flex-direction: row;
        max-width: ${rem(400)};
      }

      @media ${media.stMidUp} {
        max-width: ${rem(584)};
      }

      .product-entry-hero__link {
        height: ${rem(40)};
        min-width: ${rem(190)};
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: ${rem(4)};
        // padding: 0 ${rem(32)};
        color: white;
        text-decoration: none;
        cursor: pointer;
      }

      .product-entry-hero__anchor {
        /* background: transparent; */
        text-decoration: none;
        color: var(--color-white);
        width: 100%;
      }
    }
  }
`
